import { Box } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { DealsFilters } from '../components/DealsFilters';
import { FilterOption } from '../components/DealsFilters/DealsFilters.types';
import { useDealsFilters } from '../components/DealsFilters/hooks/useDealsFilters';
import { useOwnerOptions } from '../components/DealsFilters/hooks/useOwnerOptions';
import { usePipelineOptions } from '../components/DealsFilters/hooks/usePipelineOptions';
import { DealsKanban } from '../components/DealsKanban';
import { DealsStatistics } from '../components/DealsStatistics';
import { DealsTable } from '../components/DealsTable';

export const enum LayoutType {
  LIST = 'list',
  KANBAN = 'kanban',
}

interface DealsMainProps {
  userId?: string;
}

export const DealsMain = ({ userId }: DealsMainProps) => {
  const currentRoleViewType =
    (sessionStorage.getItem('currentRoleViewType') as LayoutType) || LayoutType.KANBAN;
  const [layout, setLayout] = useState<LayoutType>(currentRoleViewType);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [needsAttention, setNeedsAttention] = useState(false);

  const { filterState, updateFilter } = useDealsFilters();
  const { selectedPipelines, setSelectedPipelines } = usePipelineOptions();
  const { ownerOptions } = useOwnerOptions();

  useEffect(() => {
    if (userId && ownerOptions.length > 0) {
      const owner = ownerOptions.find((opt: FilterOption) => opt.Id === userId);
      if (owner) {
        const currentOwnerId = filterState.owner?.Id;
        if (currentOwnerId !== userId) {
          updateFilter({ owner });
        }
      }
    }
  }, [userId, ownerOptions, updateFilter]);

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('currentRoleViewType', newValue);
    setLayout(newValue);
  };

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '24px',
        gap: '24px',
      }}
    >
      <DealsStatistics mine={tabValue === 0} />
      <DealsFilters
        onLayoutChange={handleLayoutChange}
        layout={layout}
        filterState={filterState}
        onFilterChange={updateFilter}
        selectedPipelines={selectedPipelines}
        onPipelinesChange={setSelectedPipelines}
        tabValue={tabValue}
        onTabChange={handleTabChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        needsAttention={needsAttention}
        onNeedsAttentionChange={setNeedsAttention}
      />
      {layout === LayoutType.KANBAN ? (
        <DealsKanban
          filterState={filterState}
          selectedPipelines={selectedPipelines}
          tabValue={tabValue}
          searchTerm={searchTerm}
          needsAttention={needsAttention}
        />
      ) : (
        <DealsTable
          filterState={filterState}
          selectedPipelines={selectedPipelines}
          tabValue={tabValue}
          searchTerm={searchTerm}
          needsAttention={needsAttention}
        />
      )}
    </Box>
  );
};
